// src/config/categories.js
// Categories for the Resources Hub.
//
// There are two kinds of category:
//
//   • BUILT-IN — a fixed, ordered set with special behaviour that the code
//     depends on. These are NOT admin-editable:
//       - 'internal'   hosts the built-in Email Signature Tool
//       - 'thirdparty' renders as a table (Team column) and is what scoped
//         admins' optional "team limit" narrows.
//
//   • CUSTOM — extra dashboard tabs (e.g. "HR") created at runtime by SUPER
//     admins, stored in the `hub_categories` Firestore collection (see
//     src/data/categories.js). Custom categories always render as a normal
//     tile grid and carry no special behaviour.
//
// Use mergeCategories(customRows) to get the full ordered list the Hub shows.

export const BUILTIN_CATEGORIES = [
  { key: 'sales', label: 'Sales', builtin: true },
  { key: 'product', label: 'Product', builtin: true },
  { key: 'internal', label: 'Internal Tools', builtin: true },
  { key: 'thirdparty', label: 'Third Party Tools', builtin: true },
];

// Back-compat alias. Existing imports of CATEGORIES keep working as the
// built-in set; prefer mergeCategories() anywhere custom categories matter.
export const CATEGORIES = BUILTIN_CATEGORIES;

// Keys a custom category may never reuse (they collide with built-in behaviour).
export const RESERVED_CATEGORY_KEYS = BUILTIN_CATEGORIES.map((c) => c.key);

// Turn a human label ("People Ops") into a stable url/doc-id key ("people-ops").
export function slugifyCategoryKey(label) {
  return (label || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

// Merge the built-in categories (fixed order, always first) with custom
// categories from Firestore (sorted by `order`, then label). Reserved-key or
// empty rows are dropped. Returns the ordered list used for tabs everywhere.
export function mergeCategories(customRows = []) {
  const custom = (customRows || [])
    .map((r) => ({
      key: r.key || r.id,
      label: r.label || r.key || r.id,
      order: typeof r.order === 'number' ? r.order : 0,
      builtin: false,
    }))
    .filter((c) => c.key && !RESERVED_CATEGORY_KEYS.includes(c.key))
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  return [...BUILTIN_CATEGORIES, ...custom];
}

// Label for a category key. Pass the merged list when custom categories may be
// involved; defaults to the built-in set so old call sites still resolve those.
export function getCategoryLabel(key, categories = BUILTIN_CATEGORIES) {
  const found = categories.find((c) => c.key === key);
  return found ? found.label : key;
}
